import BaseService from './BaseService';
import * as apiroutes from '../apiRoutes';
import axiosInstance from './axios';
import localStorageService from './localStorageService';

class FinancialReportService extends BaseService {
    constructor() {
        super(apiroutes.FINANCIAL_REPORTS_SALES);
    }

    /**
     * Get sales report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     * @param {string} groupBy  - "day" | "week" | "month"
     */
    async getSalesReport(fromDate, toDate, groupBy = 'day') {
        return await super.customCall('GET', '', null, { fromDate, toDate, groupBy }, null);
    }

    /**
     * Get Profit & Loss report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     */
    async getProfitLossReport(fromDate, toDate) {
        const companyId = await this.getCompanyId();
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: apiroutes.FINANCIAL_REPORTS_PROFIT_LOSS,
                params: { companyId, fromDate, toDate },
                headers: { 'Content-Type': 'application/json' },
            });
            return { success: true, data: response.data, status: response.status };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getErrorMessage(error),
            };
        }
    }

    /**
     * Get Cash Flow report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     */
    async getCashFlowReport(fromDate, toDate) {
        const companyId = await this.getCompanyId();
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: apiroutes.FINANCIAL_REPORTS_CASH_FLOW,
                params: { companyId, fromDate, toDate },
                headers: { 'Content-Type': 'application/json' },
            });
            return { success: true, data: response.data, status: response.status };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getErrorMessage(error),
            };
        }
    }

    /**
     * Get Financial Summary report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     */
    async getFinancialSummaryReport(fromDate, toDate) {
        const companyId = await this.getCompanyId();
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: apiroutes.FINANCIAL_REPORTS_SUMMARY,
                params: { companyId, fromDate, toDate },
                headers: { 'Content-Type': 'application/json' },
            });
            return { success: true, data: response.data, status: response.status };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getErrorMessage(error),
            };
        }
    }

    /**
     * Get Revenue by Category report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     */
    async getRevenueByCategoryReport(fromDate, toDate) {
        const companyId = await this.getCompanyId();
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: apiroutes.FINANCIAL_REPORTS_REVENUE_CATEGORY,
                params: { companyId, fromDate, toDate },
                headers: { 'Content-Type': 'application/json' },
            });
            return { success: true, data: response.data, status: response.status };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getErrorMessage(error),
            };
        }
    }

    /**
     * Get COGS report data
     * @param {string} fromDate - ISO date string e.g. "2024-01-01"
     * @param {string} toDate   - ISO date string e.g. "2024-12-31"
     */
    async getCogsReport(fromDate, toDate) {
        const companyId = await this.getCompanyId();
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: apiroutes.FINANCIAL_REPORTS_COGS,
                params: { companyId, fromDate, toDate },
                headers: { 'Content-Type': 'application/json' },
            });
            return { success: true, data: response.data, status: response.status };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data || error.message,
                status: error.response?.status,
                message: this.getErrorMessage(error),
            };
        }
    }

    // ── Download helpers (backend-generated PDF / CSV) ─────────────────────

    _triggerDownload(blobData, format, filename) {
        const ext = format === 'csv' ? 'csv' : 'pdf';
        const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
        const blob = new Blob([blobData], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.${ext}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Download an order report from backend
     * @param {'by-status'|'by-date'|'summary'} type
     * @param {'pdf'|'csv'} format
     * @param {object} params  e.g. { fromDate, toDate, status }
     */
    async downloadOrderReport(type = 'summary', format = 'pdf', params = {}) {
        const companyId = await this.getCompanyId();
        const urlMap = {
            'by-status': apiroutes.ORDER_REPORTS_BY_STATUS,
            'by-date': apiroutes.ORDER_REPORTS_BY_DATE,
            'summary': apiroutes.ORDER_REPORTS_SUMMARY,
        };
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: urlMap[type] || apiroutes.ORDER_REPORTS_SUMMARY,
                params: { companyId, format, ...params },
                responseType: 'blob',
            });
            this._triggerDownload(response.data, format, `order-${type}-report`);
            return { success: true };
        } catch (error) {
            return { success: false, message: this.getErrorMessage(error) };
        }
    }

    /**
     * Download a menu-item report from backend
     * @param {'popular'|'sales'|'summary'} type
     * @param {'pdf'|'csv'} format
     * @param {object} params  e.g. { fromDate, toDate, limit, menuItemId }
     */
    async downloadMenuItemReport(type = 'summary', format = 'pdf', params = {}) {
        const companyId = await this.getCompanyId();
        const urlMap = {
            'popular': apiroutes.MENUITEM_REPORTS_POPULAR,
            'sales': apiroutes.MENUITEM_REPORTS_SALES,
            'summary': apiroutes.MENUITEM_REPORTS_SUMMARY,
        };
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: urlMap[type] || apiroutes.MENUITEM_REPORTS_SUMMARY,
                params: { companyId, format, ...params },
                responseType: 'blob',
            });
            this._triggerDownload(response.data, format, `menuitem-${type}-report`);
            return { success: true };
        } catch (error) {
            return { success: false, message: this.getErrorMessage(error) };
        }
    }
}

export default new FinancialReportService();
